'use client';

/**
 * Parallel project sessions — one live Soma session per project, side by
 * side. This is the ONE chat surface: the legacy classic chat (/classic)
 * was folded in here as the pinned "⚡ Beast" entry.
 *
 * Rail (collapsible like the main sidebar):
 *  - ⚡ Beast pinned on top — the legacy main-session Chat surface
 *    (project selector, slash commands, scratchpad), never closable
 *  - 🦾 General — the main Beast session on the sessions machinery
 *  - one row per project session (status dot: green pulse = working)
 * Picker: existing projects · CREATE a new project (same as the Projects
 * page: DB row + folder) · any absolute folder path.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Chat } from '@/components/chat/Chat';
import { SessionChat } from '@/components/chat/SessionChat';
import { projectsApi } from '@/lib/api';
import { CREATE_PLACES, CREATE_PLACE_GROUPS } from '@/lib/business-projects';
import { playPing } from '@/lib/sessionPing';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

const GW = '/api/gw';
const GENERAL = '__general';
const BEAST = '__beast'; // pinned classic main chat (folded-in /classic)

interface SessionInfo {
  slug: string;
  name: string;
  path: string;
  icon?: string;
  state: 'working' | 'idle' | 'hibernating';
  lastActiveAt?: number;
  model?: { id: string } | null;
  contextUsage?: { tokens: number; contextWindow: number; percent: number } | null;
  lastError?: string;
}

interface ProjectRow { slug: string; name?: string; path: string; icon?: string }

interface PhaseData {
  stage: number;
  label: string;       // "Design Handoff"
  short: string;       // "Design"
  color: string;       // current-stage color
  progress: number;    // 0-100
  step: string;        // current_step text (tooltip)
  status: string;      // approved | awaiting_boss_approval | inferred | ...
  barGradient: string; // CSS gradient Stage-0 → current stage
  inferred?: boolean;  // no phase.json — stage estimated from files
}

export default function SessionsPage() {
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [phases, setPhases] = useState<Record<string, PhaseData>>({});
  const [mainBusy, setMainBusy] = useState(false);
  // Start with SSR-stable defaults; hydrate the saved values in an effect.
  // Reading localStorage in the initializer causes a server/client hydration
  // mismatch (server can't see storage) — Next 16 regenerates the tree.
  // selected starts as null = "not hydrated yet". Defaulting to GENERAL meant
  // any remount (Electron renderer auto-reload, React remount) flashed the
  // pane to General before localStorage restored the real session — the
  // "Beast keeps switching to General" bug. null → show a neutral loader, then
  // restore the saved selection. (SSR-safe: server + first client render match.)
  const [selected, setSelected] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  useEffect(() => {
    try {
      setSelected(localStorage.getItem('beast:sessions-selected') || GENERAL);
      setCollapsed(localStorage.getItem('beast:sessions-rail-collapsed') === '1');
      // beast:sessions-order (manual drag arrangement) retired 2026-09-01 —
      // Boss: recency ordering made it redundant, "just remove manual change
      // of order of sessions ability so there is no conflict".
      localStorage.removeItem('beast:sessions-order');
    } catch { setSelected(GENERAL); }
  }, []);

  // rail order: RECENCY — last worked-on at the top (Boss, 2026-09-01:
  // "The chats should be organized as the last worked on is at the top").
  // Drag-to-reorder removed the same day at Boss's request — one ordering
  // rule, no conflicts. Anchors stay pinned first: the main Beast chat and
  // General are the two rows muscle memory reaches for.
  const anchor = (s: SessionInfo) => (s.slug === BEAST ? 0 : s.slug === GENERAL ? 1 : 2);
  const ordered = [...sessions].sort((a, b) => {
    const aa = anchor(a), ab = anchor(b);
    if (aa !== ab) return aa - ab;
    return (b.lastActiveAt || 0) - (a.lastActiveAt || 0);
  });
  // ...then float LIVE work above even recent-but-finished sessions. A
  // working session's lastActiveAt only updates when its turn ENDS, so
  // without this lift a long-running build would sink below chats you
  // touched after it started.
  const rank = (s: SessionInfo) =>
    anchor(s) < 2 ? 0 : s.state === 'working' ? 1 : 2;
  const displayed = [...ordered].sort((a, b) => rank(a) - rank(b));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [customPath, setCustomPath] = useState('');
  const [newName, setNewName] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  // ONE place picker — mirrors the sidebar (Businesses / Collabs / Other).
  const [newPlace, setNewPlace] = useState('wtf');
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState<string | null>(null);

  // Mobile (Telegram mini-app): the rail is a slide-over, closed by default —
  // on a 390px phone the always-open rail squeezed the chat to ~140px.
  const [mobileRailOpen, setMobileRailOpen] = useState<boolean>(false);

  const select = (slug: string) => {
    setSelected(slug);
    setMobileRailOpen(false);
    try { localStorage.setItem('beast:sessions-selected', slug); } catch {}
  };
  // Belt-and-suspenders: ALWAYS persist whatever is selected (covers any path
  // that sets it, not just clicks) so a remount/navigation always restores it.
  useEffect(() => {
    if (selected) { try { localStorage.setItem('beast:sessions-selected', selected); } catch {} }
  }, [selected]);
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem('beast:sessions-rail-collapsed', c ? '0' : '1'); } catch {}
      return !c;
    });
  };

  // Per-session status from the last poll, so we can ping on the moment a
  // dot flips to green (finished → ready) or red (terminated / error).
  // Seeded lazily: the very first poll establishes a baseline and never pings.
  const prevStatus = useRef<Map<string, { working: boolean; error: boolean }> | null>(null);
  const mainBusyRef = useRef(false);
  // Latest sessions, for the SLOW phase poller (decoupled from the 5s status poll).
  const phaseSessionsRef = useRef<SessionInfo[]>([]);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${GW}/sessions`);
      const d = await r.json();
      if (d.ok) {
        // Pipeline worker sessions (worker.py opens one per check batch) are
        // internal machinery, not chats — Boss: "hide those chats since I
        // don't need to communicate with them". Filter at ingestion so the
        // rail, the status pings and the phase poller all ignore them.
        d.sessions = (d.sessions as SessionInfo[]).filter(
          (s) => !/^worker-/.test(s.slug));
        const next = new Map<string, { working: boolean; error: boolean }>();
        for (const s of d.sessions as SessionInfo[]) {
          next.set(s.slug, { working: s.state === 'working', error: !!s.lastError });
        }
        const prev = prevStatus.current;
        if (prev) {
          for (const [slug, cur] of next) {
            const was = prev.get(slug);
            if (!was) continue;
            // error newly appeared → red ping (terminated / rate limit / API)
            if (cur.error && !was.error) playPing('error');
            // was working, now stopped with no error → green ping (ready for next)
            else if (was.working && !cur.working && !cur.error) playPing('done');
          }
        }
        prevStatus.current = next;
        setSessions(d.sessions);
        // Phase tags are polled SEPARATELY + slowly (see the phase effect below).
        // They USED to be fetched here every 5s, but /api/phase shells out to git
        // + gh per project and can block the dev server for seconds — freezing the
        // whole UI on every poll (read as "the window keeps reloading"). Decoupled.
        phaseSessionsRef.current = d.sessions;
      }
    } catch { /* gateway down — health UI elsewhere */ }
    try {
      // Electron: the main chat session lives in the Electron MAIN process, not the
      // gateway — polling the gateway could never see it working. Ask over IPC there.
      // 'busy' covers tool-call gaps (isStreaming flickers false mid-work, 2026-09-04).
      const beastIpc = (typeof window !== 'undefined' ? (window as unknown as { beast?: { invoke: (ch: string) => Promise<unknown> } }).beast : undefined);
      const s = beastIpc
        ? (await beastIpc.invoke('session')) as { busy?: boolean; isStreaming?: boolean }
        : await fetch(`${GW}/session`).then((r) => r.json());
      const busy = !!(s.busy ?? s.isStreaming);
      // General (main) session: ping green when it finishes a turn
      if (mainBusyRef.current && !busy) playPing('done');
      mainBusyRef.current = busy;
      setMainBusy(busy);
    } catch {}
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  // Phase tags poll on a SLOW, SEPARATE timer (every 30s) — NOT in the 5s status
  // loop. /api/phase shells out to git + `gh api` (network) per project and can
  // block for seconds; doing that every 5s froze the entire window. Phase only
  // changes per commit, so 30s is plenty. Reads sessions via ref so it doesn't
  // restart on every status poll.
  useEffect(() => {
    let cancelled = false;
    const pullPhases = async () => {
      const paths: Record<string, string> = {};
      for (const s of phaseSessionsRef.current) if (s.path) paths[s.slug] = s.path;
      if (Object.keys(paths).length === 0) return;
      try {
        const pd = await fetch('/api/phase', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paths }),
        }).then((r) => r.json());
        if (!cancelled && pd?.phases) setPhases(pd.phases);
      } catch { /* phase data optional */ }
    };
    const t = setInterval(pullPhases, 30_000);
    const warm = setTimeout(pullPhases, 4000); // first pull shortly after initial status load
    return () => { cancelled = true; clearInterval(t); clearTimeout(warm); };
  }, []);


  useEffect(() => {
    if (!pickerOpen) return;
    (projectsApi.list() as Promise<any>)
      .then((d: any) => {
        const all = Array.isArray(d) ? d : d?.projects || [];
        const rows: ProjectRow[] = all
          .filter((p: any) => p.path)
          .map((p: any) => ({ slug: p.slug, name: p.name || p.slug, path: p.path, icon: p.icon }));
        setProjects(rows);
      })
      .catch(() => setProjects([]));
  }, [pickerOpen]);

  const openSession = async (slug: string, path: string, name?: string, icon?: string, create = false) => {
    setOpening(slug);
    setError(null);
    try {
      const r = await fetch(`${GW}/sessions/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, path, name, icon, create }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'Failed to open session');
      await refresh();
      select(d.session.slug);
      setPickerOpen(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setOpening(null);
    }
  };

  // create a brand-new project (same flow as the Projects page) + open it
  const createProject = async () => {
    const name = newName.trim();
    if (!name) return;
    setOpening('__new');
    setError(null);
    try {
      const place = CREATE_PLACES.find((x) => x.key === newPlace)!;
      const slugified = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      // name already taken? then the user means the EXISTING project — open it
      const existing = projects.find((p) => p.slug === slugified);
      if (existing) {
        setNewName('');
        // create:true — if the project's folder is missing (deleted, moved), the
        // gateway recreates it instead of throwing 'path does not exist'.
        await openSession(existing.slug, existing.path, existing.name, existing.icon, true);
        return;
      }
      // Always the HTTP route (NOT projectsApi.create): in Electron that
      // helper switches to the legacy IPC handler, which skips the real-tree
      // path mapping and directory creation.
      const r = await fetch('/api/projects', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, category: place.category, collab: place.collab }),
      });
      const d: any = await r.json();
      if (!d?.ok || !d.project) {
        if (/UNIQUE constraint/i.test(d?.error || '')) throw new Error(`A project named "${name}" already exists — pick it from the Projects list below.`);
        throw new Error(d?.error || 'Project creation failed');
      }
      const slug = d.project.slug;
      setNewName('');
      await openSession(slug, d.project.path, name, undefined, true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setOpening(null);
    }
  };

  // rename = display name everywhere (session registry + project DB row);
  // slug and folder are stable identity and never change. Inline editor —
  // window.prompt() is not supported in Electron renderers.
  const [renaming, setRenaming] = useState<{ slug: string; value: string } | null>(null);
  const startRename = (slug: string, currentName: string) => {
    if (collapsed) toggleCollapsed();
    setRenaming({ slug, value: currentName });
  };
  const commitRename = async () => {
    if (!renaming) return;
    const { slug, value } = renaming;
    const name = value.trim();
    setRenaming(null);
    const current = sessions.find((s) => s.slug === slug)?.name;
    if (!name || name === current) return;
    try {
      await fetch(`${GW}/sessions/${slug}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      // keep the Projects page in sync (HTTP route — not the legacy IPC)
      await fetch(`/api/projects/${slug}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      }).catch(() => {});
      refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const closeSession = async (slug: string) => {
    await fetch(`${GW}/sessions/${slug}`, { method: 'DELETE' }).catch(() => {});
    if (selected === slug) select(GENERAL);
    refresh();
  };

  const sel = sessions.find((s) => s.slug === selected) || null;

  // Status dot — three colors only (Boss's model):
  //   gray (pulsing) = working, a run is in progress
  //   green          = finished, waiting for the next instruction
  //   red            = terminated / error / rate limit — needs attention
  // NOTE: a hibernating session is just an idle one whose context was released
  // to save cost — to the user it is still "done, waiting for you" → GREEN.
  const Dot = ({ state, error, hibernating }: { state: 'working' | 'idle'; error?: boolean; hibernating?: boolean }) => {
    // working → a real spinning ring (unmistakable at a glance), not a faint pulse
    if (!error && state === 'working') {
      return <span className="w-3 h-3 border-[1.5px] border-gray-600 border-t-gray-200 rounded-full animate-spin shrink-0" title="working…" />;
    }
    const { cls, title } = error
      ? { cls: 'bg-red-500', title: 'terminated / error — needs attention' }
      : { cls: 'bg-emerald-400', title: hibernating
            ? 'done — waiting for the next instruction (hibernating; wakes instantly on message)'
            : 'done — ready for the next instruction' };
    return <span className={`w-2 h-2 rounded-full shrink-0 ${cls}`} title={title} />;
  };

  return (
    <div className="flex h-screen bg-[#0a0a0f] text-gray-100">
      {/* Mobile backdrop — tap to dismiss the slide-over rail */}
      {mobileRailOpen && (
        <div className="md:hidden fixed inset-0 bg-black/60 z-30" onClick={() => setMobileRailOpen(false)} />
      )}
      {/* Sessions rail — static column on md+, slide-over on mobile */}
      <aside className={`${mobileRailOpen ? 'flex' : 'hidden'} md:flex fixed md:static inset-y-0 left-0 z-40 md:z-auto w-72 ${collapsed ? 'md:w-12' : 'md:w-60'} bg-[#0a0a0f] shrink-0 border-r border-white/10 flex-col transition-[width] duration-200`}>
        <div className={`py-3 border-b border-white/10 flex items-center ${collapsed ? 'justify-center px-0' : 'justify-between px-4'}`}>
          {!collapsed && <span className="text-sm font-bold tracking-wide">Sessions</span>}
          <div className="flex items-center gap-1.5">
            {!collapsed && (
              <button onClick={() => setPickerOpen(!pickerOpen)}
                className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20">+ Open</button>
            )}
            <button onClick={toggleCollapsed} aria-label={collapsed ? 'Expand sessions rail' : 'Collapse sessions rail'}
              className="p-1 rounded text-gray-500 hover:text-gray-200 hover:bg-white/10">
              {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {pickerOpen && !collapsed && (
          <div className="border-b border-white/10 max-h-96 overflow-y-auto">
            <div className="px-3 pt-2 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-gray-500">Open a session</span>
              <button onClick={() => { setPickerOpen(false); setError(null); }} aria-label="Close picker"
                className="text-gray-500 hover:text-gray-200 text-sm px-1.5 py-0.5 rounded hover:bg-white/10">✕</button>
            </div>
            {error && (
              <div className="mx-3 mt-2 px-2.5 py-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/20 rounded">
                {error}
              </div>
            )}
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-500">New project</div>
            <div className="px-3 pb-2 flex flex-col gap-1">
              <div className="flex gap-1">
                <input value={newName} onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') createProject(); }}
                  placeholder="Project name…"
                  className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-[11px] outline-none placeholder-gray-600" />
                <button onClick={createProject} disabled={!newName.trim() || opening === '__new'}
                  className="text-[11px] px-2 rounded bg-emerald-600/30 text-emerald-200 hover:bg-emerald-600/50 disabled:opacity-40">
                  {opening === '__new' ? '…' : 'Create'}
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-600">in</span>
                <select value={newPlace} onChange={(e) => setNewPlace(e.target.value)}
                  className="flex-1 bg-white/5 border border-white/10 rounded px-1.5 py-1 text-[11px] text-gray-300 outline-none">
                  {CREATE_PLACE_GROUPS.map((g) => (
                    <optgroup key={g} label={g} className="bg-gray-900">
                      {CREATE_PLACES.filter((p) => p.group === g).map((p) => (
                        <option key={p.key} value={p.key} className="bg-gray-900">{g === 'Collabs' ? `Collabs → ${p.label}` : p.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-3 pt-1 pb-1 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-gray-500 shrink-0">Projects</span>
              <input value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)}
                placeholder="search…"
                className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px] outline-none placeholder-gray-600" />
            </div>
            {projects
              .filter((p) => {
                const q = projectSearch.trim().toLowerCase();
                if (!q) return true;
                return (p.name || '').toLowerCase().includes(q) || p.slug.toLowerCase().includes(q);
              })
              .map((p) => (
              <button key={p.slug} onClick={() => openSession(p.slug, p.path, p.name, p.icon)}
                disabled={opening === p.slug}
                className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2 disabled:opacity-50">
                <span>{p.icon || '📁'}</span>
                <span className="truncate flex-1">{p.name}</span>
                {opening === p.slug && <span className="w-3 h-3 border border-gray-500 border-t-gray-200 rounded-full animate-spin" />}
              </button>
            ))}
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-500">Any folder</div>
            <div className="px-3 pb-3 flex gap-1">
              <input value={customPath} onChange={(e) => setCustomPath(e.target.value)}
                placeholder="/absolute/path/to/project"
                className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-[11px] outline-none placeholder-gray-600" />
              <button
                onClick={() => {
                  const p = customPath.trim();
                  if (!p) return;
                  const slug = p.split('/').filter(Boolean).pop() || 'project';
                  openSession(slug.toLowerCase().replace(/[^a-z0-9._-]/g, '-'), p, slug);
                }}
                className="text-[11px] px-2 rounded bg-white/10 hover:bg-white/20">Go</button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto py-1">
          {/* Beast — the classic main chat (former /classic), pinned on top */}
          <div
            className={`flex items-center gap-2.5 cursor-pointer ${collapsed ? 'justify-center px-0 py-3' : 'px-4 py-2.5'} ${selected === BEAST ? 'bg-white/10' : 'hover:bg-white/5'}`}
            onClick={() => select(BEAST)} title="Beast — main chat (classic surface)">
            <Dot state={mainBusy ? 'working' : 'idle'} />
            {!collapsed && (
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold truncate">⚡ Beast</span>
                <span className="block text-[10px] text-gray-500 truncate">{mainBusy ? 'working…' : 'main chat'}</span>
              </span>
            )}
          </div>

          {/* General — the main Beast session, pinned, never closable */}
          <div
            className={`flex items-center gap-2.5 cursor-pointer ${collapsed ? 'justify-center px-0 py-3' : 'px-4 py-2.5'} ${selected === GENERAL ? 'bg-white/10' : 'hover:bg-white/5'}`}
            onClick={() => select(GENERAL)} title="General — main Beast session">
            <Dot state={mainBusy ? 'working' : 'idle'} />
            {!collapsed && (
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold truncate">🦾 General</span>
                <span className="block text-[10px] text-gray-500 truncate">{mainBusy ? 'working…' : 'main Beast session'}</span>
              </span>
            )}
          </div>

          {displayed.map((s) => (
            <div key={s.slug}
              className={`group flex items-center gap-2.5 cursor-pointer ${collapsed ? 'justify-center px-0 py-3' : 'px-4 py-2.5'} ${selected === s.slug ? 'bg-white/10' : 'hover:bg-white/5'}`}
              onClick={() => select(s.slug)}
              onDoubleClick={() => startRename(s.slug, s.name)}
              title={`${s.name}${s.model?.id ? ` · ${s.model.id}` : ''} — double-click to rename`}>
              <Dot state={s.state === 'working' ? 'working' : 'idle'} error={!!s.lastError} hibernating={s.state === 'hibernating'} />
              {!collapsed && renaming?.slug === s.slug ? (
                <input autoFocus value={renaming.value}
                  onChange={(e) => setRenaming({ slug: s.slug, value: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(null); }}
                  onBlur={commitRename}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 min-w-0 bg-white/10 border border-cyan-400/50 rounded px-1.5 py-1 text-xs text-gray-100 outline-none" />
              ) : !collapsed && (
                <>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs font-semibold truncate">{s.icon ? `${s.icon} ` : ''}{s.name}</span>
                      {/* colored stage tag — which factory stage this project is at */}
                      {phases[s.slug] && (
                        <span className="shrink-0 text-[8px] font-bold uppercase tracking-wide px-1 py-px rounded leading-none"
                          style={{ background: `${phases[s.slug].color}26`, color: phases[s.slug].color, border: `1px solid ${phases[s.slug].color}59` }}
                          title={`Stage ${phases[s.slug].stage} · ${phases[s.slug].label} · ${phases[s.slug].progress}% complete${phases[s.slug].status ? ` (${phases[s.slug].status})` : ''}${phases[s.slug].step ? `\n${phases[s.slug].step}` : ''}`}>
                          {phases[s.slug].short}
                        </span>
                      )}
                    </span>
                    <span className="block text-[10px] text-gray-500 truncate">
                      {s.state === 'working' ? 'working…' : s.state === 'hibernating' ? `💤 ${s.model?.id?.replace('claude-', '') || ''}` : s.model?.id?.replace('claude-', '') || 'idle'}
                      {s.contextUsage?.contextWindow ? ` · ◉${Math.round(s.contextUsage.percent ?? (s.contextUsage.tokens / s.contextUsage.contextWindow) * 100)}%` : ''}
                    </span>
                    {/* progress bar — fills Stage-0 color → current stage color;
                        completion % sits at the END of the bar (kept away from
                        the ◉ context-window % above so they aren't confused). */}
                    {phases[s.slug] && (
                      <span className="flex items-center gap-1.5 mt-1">
                        <span className="flex-1 h-[3px] rounded-full bg-white/10 overflow-hidden">
                          <span className="block h-full rounded-full transition-all duration-500"
                            style={{ width: `${phases[s.slug].progress}%`, background: phases[s.slug].barGradient }} />
                        </span>
                        <span className="text-[8px] leading-none tabular-nums shrink-0 text-gray-400"
                          title={phases[s.slug].inferred ? 'estimated from project files (no phase tracker)' : 'from phase tracker'}>
                          {phases[s.slug].inferred ? '~' : ''}{phases[s.slug].progress}%
                        </span>
                      </span>
                    )}
                  </span>
                  {/* two-step close: first ✕ arms it, then "Close?" confirms.
                      Native confirm() is unreliable in Electron, and a single
                      ✕ was too easy to mis-click. */}
                  {confirmClose === s.slug ? (
                    <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => { closeSession(s.slug); setConfirmClose(null); }}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/25 text-red-200 hover:bg-red-500/40">Close?</button>
                      <button onClick={() => setConfirmClose(null)}
                        className="text-[10px] text-gray-500 hover:text-gray-300">cancel</button>
                    </span>
                  ) : (
                    <button onClick={(e) => { e.stopPropagation(); setConfirmClose(s.slug); }}
                      title="Close session (history is kept; reopen anytime from + Open)"
                      className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-300 text-xs px-1 shrink-0">✕</button>
                  )}
                </>
              )}
            </div>
          ))}

          {sessions.length === 0 && !collapsed && (
            <div className="px-4 py-4 text-xs text-gray-500 leading-relaxed">
              Hit <b className="text-gray-300">+ Open</b> to add project sessions — each runs in
              parallel with General and keeps working while you look at the others.
            </div>
          )}
        </div>

      </aside>

      {/* Chat panel */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar — opens the sessions rail */}
        <div className="md:hidden flex items-center gap-2 px-3 py-2 border-b border-white/10 shrink-0">
          <button onClick={() => setMobileRailOpen(true)} aria-label="Open sessions"
            className="p-1.5 rounded text-gray-400 hover:text-gray-100 hover:bg-white/10">
            <PanelLeftOpen className="w-5 h-5" />
          </button>
          <span className="text-sm font-semibold truncate">
            {selected === BEAST ? 'Beast' : selected === GENERAL ? 'General' : sel?.name || 'Sessions'}
          </span>
        </div>
        <div className="flex-1 min-h-0">
        {selected === null ? (
          <div className="h-full flex items-center justify-center text-gray-600 text-sm">Restoring your session…</div>
        ) : selected === BEAST ? (
          <Chat key={BEAST} />
        ) : selected === GENERAL ? (
          <SessionChat key={GENERAL} slug="general" name="General" main />
        ) : sel ? (
          <SessionChat key={sel.slug} slug={sel.slug} name={sel.name} onRename={() => startRename(sel.slug, sel.name)} />
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500 text-sm">
            Pick a session on the left
          </div>
        )}
        </div>
      </main>
    </div>
  );
}
